import { useEffect } from 'react';

// Client-side SEO / AEO / GEO helper. React Router keeps the app on one
// document, so each route sets its own <title>, meta description, and
// JSON-LD structured data while it's mounted, and restores the previous
// values on unmount. Structured data (schema.org) is what answer engines
// and generative engines read to quote a page accurately.
//
// Pass `jsonLd` as a STABLE reference (module-level const, or state) — a
// fresh object literal every render re-runs the effect each render.
export default function usePageSeo({ title, description, jsonLd } = {}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute('content') : null;
    if (meta && description) meta.setAttribute('content', description);

    const blocks = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
    const nodes = blocks.map((block) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.dataset.seo = '1';
      s.textContent = JSON.stringify(block);
      document.head.appendChild(s);
      return s;
    });

    return () => {
      document.title = prevTitle;
      if (meta && prevDesc !== null) meta.setAttribute('content', prevDesc);
      nodes.forEach((n) => n.remove());
    };
  }, [title, description, jsonLd]);
}

// schema.org MusicEvent for UNDERGROUNDJUUG FEST — shared by /events and
// /tickets so either URL can be quoted for "when / where / who / tickets".
export function buildEventJsonLd({ name, dateISO, address, artists = [], ticketUrl }) {
  const evt = {
    '@context': 'https://schema.org',
    '@type': 'MusicEvent',
    name: name || 'UNDERGROUNDJUUG FEST',
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
  };
  if (dateISO) evt.startDate = dateISO;
  if (address) {
    evt.location = {
      '@type': 'Place',
      name: address,
      address: { '@type': 'PostalAddress', streetAddress: address },
    };
  }
  if (artists.length) {
    evt.performer = artists.map((a) => ({ '@type': 'MusicGroup', name: a.name || a.artist }));
  }
  if (ticketUrl) {
    evt.offers = {
      '@type': 'Offer',
      url: ticketUrl,
      availability: 'https://schema.org/InStock',
      priceCurrency: 'USD',
    };
  }
  return evt;
}
